import {
	Center,
	Flex,
	Skeleton,
	Spinner,
	useMediaQuery,
} from '@chakra-ui/react'
import React, { Suspense, useState } from 'react'
import AppContainer from '../Components/AppContainer'
import MobileScrollBar from '../Components/MobileScrollBar'
import Results, {
	Result,
} from '../Components/Results/VirtualLineResults/VirtualResultsCards'

import useProductSuggestions from '../Hooks/useProductSuggestions'
import { LightColorName, BrightNess } from '../Data/Types'
const testArray = [1, 2, 3, 4, 5]

const TallMobile = React.lazy(
	() => import('../Components/LineProjection/tallmobile'),
)
const DesktopBackground = React.lazy(
	() => import('../Components/LineProjection/LineDesktop'),
)
const DistancePicker = React.lazy(
	() => import('../Components/Pickers/DistancePicker'),
)
const ColorPicker = React.lazy(
	() => import('../Components/Pickers/ColorPicker'),
)
const BrightnessPicker = React.lazy(
	() => import('../Components/Pickers/BrightnessPicker'),
)
const RadioPicker = React.lazy(
	() => import('../Components/Pickers/RadioPicker'),
)
const ControlPanel = React.lazy(() => import('../Components/ControlPanel'))
const VirutalLine = () => {
	const [color, setColor] = useState<LightColorName>('none')
	const [lines, setLines] = useState('1')
	const [units, setUnits] = useState('feet')
	const [lineLength, setLinelength] = useState(3)
	const [isAutomated, setIsAutomated] = useState('no')
	const [mountingDistance, setMountingDistance] = useState(5)
	const [brightNess, setBrightNess] = useState<BrightNess>({
		value: 200,
		unit: 'lux',
	})

	const disabled = color === 'none'
	const isMobile = useMediaQuery('(max-width: 768px)')[0]
	useProductSuggestions(
		{
			lightColor: color,
			numLines: Number(lines),
			lineLength,
			mountingDistance,
			ambientLightingConditions: brightNess,
		},
		disabled,
	)
	const results: Result[] = testArray.map((item) => ({
		color: color,
		brightness: brightNess,
		projectors: lineLength > 40 ? 2 : 1,
		lines: Number(lines) as 1 | 2,
		lineLength: lineLength,
	}))

	return (
		<Flex
			pb="2rem"
			overflow="hidden"
			width="100%"
			gap="1rem"
			rowGap={'2rem'}
			flexDir={'column'}
		>
			<AppContainer>
				<Flex
					position="absolute"
					height={{ base: '95%', md: '100%' }}
					top={0}
					alignSelf={'center'}
					w="100%"
					justifyContent="center"
					direction="column"
				>
					{isMobile ? (
						<Suspense fallback={<Spinner alignSelf="center" />}>
							<TallMobile colorName={color} isShown={lines === '2'} />
						</Suspense>
					) : (
						<Suspense fallback={<Spinner alignSelf="center" size={'xl'} />}>
							<DesktopBackground isShown={lines === '2'} colorName={color} />
						</Suspense>
					)}
				</Flex>
				<Suspense
					fallback={
						<Skeleton
							borderRadius={'xl'}
							startColor="orange.100"
							endColor="orange.700"
							mb={{ base: 0, md: '1rem' }}
							height={{ base: '7rem', sm: '8rem', md: '9rem' }}
							width={{ base: '100%', md: '60%' }}
							marginLeft={'1rem'}
							alignSelf={'center'}
						/>
					}
				>
					<ControlPanel>
						<ColorPicker color={color} setColor={setColor} />
						<RadioPicker
							disabled={disabled}
							options={[
								{ value: '1', label: '1 Line' },
								{ value: '2', label: '2 Lines' },
							]}
							title="Quantity"
							value={lines}
							onChange={setLines}
						/>
						<RadioPicker
							disabled={disabled}
							options={[
								{ value: 'metres', label: 'Metres' },
								{ value: 'feet', label: 'Feet' },
							]}
							title="Units"
							value={units}
							onChange={setUnits}
						/>
						<RadioPicker
							title={'Automate Using Sensors'}
							disabled={disabled}
							options={[
								{ value: 'no', label: 'No' },
								{ value: 'yes', label: 'Yes' },
							]}
							onChange={setIsAutomated}
							value={isAutomated}
							forwardFlexProps={{ width: '10rem', maxWidth: '10rem' }}
							forwardHeadingProps={{ textAlign: 'center' }}
						/>
						<DistancePicker
							disabled={disabled}
							changeValue={0.5}
							defaultValue={10}
							min={units === 'metres' ? 3 : 10}
							max={units === 'metres' ? 20 : 40}
							value={mountingDistance}
							onChange={setMountingDistance}
							title="Mounting Height"
							units={units}
						/>
						<DistancePicker
							disabled={disabled}
							changeValue={0.5}
							defaultValue={10}
							min={1}
							max={1000000}
							value={lineLength}
							onChange={setLinelength}
							title="Desired Line Length"
							units={units}
						/>
						<BrightnessPicker
							disabled={disabled}
							brightness={brightNess}
							setBrightness={setBrightNess}
						/>
					</ControlPanel>
				</Suspense>
				<Center
					display={['show', 'show', 'none']}
					zIndex={9}
					mb={{ base: '1.25rem', sm: '2.125rem' }}
					height="1rem"
					w="100vw"
					mt={['0.5rem', '0.625rem']}
					bg="#B4B4B8"
					px={{ base: '4rem', sm: '3rem' }}
				>
					<MobileScrollBar />
				</Center>
			</AppContainer>
			{color !== 'none' && <Results isMobile={isMobile} results={results} />}
		</Flex>
	)
}

export default VirutalLine
